import { type Data, UniqueIdentifier, useDraggable } from "@dnd-kit/core";
import classNames from "classnames";
import * as React from "react";

import type { MessagePathNode, TopicNode } from "@/shared/domain/topics";

import { DragItem } from "./constants";
import styles from "./Draggable.module.css";

/**
 * Drag of a non-subtree element: a single MessagePath, a Panel, or a
 * file-browser row. `data` is generic over T; predicates downstream narrow
 * by the discriminant `type` to recover the concrete payload type.
 */
export interface BaseDraggableItem<T> extends Data {
  type: Exclude<DragItem, DragItem.TreeNode>;
  data: T;
}

/**
 * Drag of an expandable subtree (a `TopicNode` or a non-leaf `MessagePathNode`).
 * `includeLeafIds` optionally restricts the set of descendant leaves the drop
 * handler should emit (used to honor an active sidebar filter). When undefined
 * or empty, the drop handler walks the full subtree.
 *
 * Scoped to its own variant — neither MessagePath nor Panel drags carry a
 * subtree, so they have no use for a leaf-id filter.
 */
export interface TreeNodeDraggableItem extends Data {
  type: DragItem.TreeNode;
  data: TopicNode | MessagePathNode;
  includeLeafIds?: ReadonlySet<string>;
}

export type DraggableItem<T> = BaseDraggableItem<T> | TreeNodeDraggableItem;

interface DraggableProps<T> {
  as?: React.ElementType;
  item: DraggableItem<T>;
  id: UniqueIdentifier;
  className?: classNames.Argument;
}

/**
 * A wrapper that makes its children draggable within a DnD context.
 */
export function Draggable<T>(
  props: React.PropsWithChildren<DraggableProps<T>>,
) {
  const { as, children, item, id } = props;
  const { attributes, listeners, setNodeRef } = useDraggable({
    id,
    data: item,
  });

  const Component = as ?? "div";

  return (
    <Component
      className={classNames(props.className, styles.draggable)}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
    >
      {children}
    </Component>
  );
}
